import { useEffect, type ReactNode } from 'react';
import type {
  AccountType,
  AuthStatus,
  MembershipTier,
  Permission,
  PlatformRole,
  VerificationStatus,
} from '@/auth/types';
import { AUTH_STORAGE_KEY, useAuthStore } from '@/stores/authStore';

export type User = Api.Account.AccountResponse;

export type AdaptedUser = Api.Account.AccountResponse & {
  name: string;
  avatar: string;
  accountType: AccountType;
  isEnterprise: boolean;
  roles: PlatformRole[];
  membership: MembershipTier;
  verificationStatus: VerificationStatus;
  verified: boolean;
  isVip: boolean;
};

export interface AuthContextType {
  status: AuthStatus;
  isLoggedIn: boolean;
  isEnterprise: boolean;
  user: AdaptedUser | null;
  rawUser: Api.Account.AccountResponse | null;
  error: string | null;
  login: (phone: string, code: string, accountType?: AccountType) => Promise<void>;
  register: (phone: string, code: string, accountType: AccountType) => Promise<void>;
  loginPersonal: (data: Api.Account.AccountLoginRequest) => Promise<Api.Account.AccountLoginResponse>;
  loginEnterprise: (data: Api.Account.AccountLoginRequest) => Promise<Api.Account.AccountLoginResponse>;
  loginWeChatPhone: (data: Api.Account.WeChatMiniProgramPhoneLoginRequest) => Promise<Api.Account.AccountLoginResponse>;
  loginWeChatSMS: (data: Api.Account.WeChatMiniProgramSMSLoginRequest) => Promise<Api.Account.AccountLoginResponse>;
  loginAs: (user: Api.Account.AccountResponse) => void;
  updateProfile: (profile: Partial<Api.Account.AccountResponse>) => Promise<void>;
  logout: () => void;
  logoutAll: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
  can: (permission: Permission) => boolean;
  hasRole: (role: PlatformRole) => boolean;
  hasMembership: (tier: MembershipTier) => boolean;
  hasAuthorizedContactView: boolean;
  canViewAuthorizedContact: () => boolean;
}

/**
 * 保留 Provider 作为应用入口和跨标签页同步边界。
 * 实际状态由 Zustand 管理，后续业务页面无需依赖 Context。
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (token) {
      useAuthStore.getState().fetchCurrentAccount();
    }

    // 设置 1 分钟 (60000ms) 轮询刷新 accounts/me 账号信息
    const POLL_INTERVAL = 60 * 1000;
    const pollTimer = setInterval(() => {
      const activeToken = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (activeToken) {
        useAuthStore.getState().fetchCurrentAccount();
      }
    }, POLL_INTERVAL);

    const syncAcrossTabs = (event: StorageEvent) => {
      if (event.key === AUTH_STORAGE_KEY) {
        void useAuthStore.persist.rehydrate();
      }
    };
    window.addEventListener('storage', syncAcrossTabs);
    return () => {
      clearInterval(pollTimer);
      window.removeEventListener('storage', syncAcrossTabs);
    };
  }, []);

  return children;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const store = useAuthStore();
  const rawUser = store.user;

  const user: AdaptedUser | null = rawUser
    ? {
        ...rawUser,
        get name() {
          return rawUser.nick_name || rawUser.phone || rawUser.email || '用户';
        },
        get avatar() {
          const rawAvatar = (rawUser as any)?.avatar;
          if (rawAvatar && typeof rawAvatar === 'object' && rawAvatar.access_url) {
            return rawAvatar.access_url;
          }
          if (typeof rawAvatar === 'string') {
            return rawAvatar;
          }
          return rawUser.avatar_url || rawUser.avatar_asset_id || (rawUser.phone ? rawUser.phone.slice(-2) : '用户');
        },
        get accountType() {
          // 后端账号类型枚举为大写：'ENTERPRISE' (企业账号) 或 'PERSONAL' (个人账号)
          const type = (rawUser.account_type || '').toUpperCase();
          return (type === 'ENTERPRISE' ? 'ENTERPRISE' : 'PERSONAL') as AccountType;
        },
        get isEnterprise() {
          return (rawUser.account_type || '').toUpperCase() === 'ENTERPRISE';
        },
        get roles() {
          const type = (rawUser.account_type || '').toUpperCase();
          return (type === 'ENTERPRISE'
            ? ['enterprise_manager', 'client']
            : ['creator']) as PlatformRole[];
        },
        get membership() {
          const sub = rawUser.membership?.subscriptions?.[0];
          return (sub?.plan_id === 'pro' || sub?.plan_id === 'vip'
            ? 'pro'
            : sub?.plan_id === 'enterprise'
            ? 'enterprise'
            : 'free') as MembershipTier;
        },
        get verificationStatus() {
          const status = (rawUser.certification_status || '').toUpperCase();
          if (status === 'APPROVED' || status === 'PASSED' || status === 'SUCCESS') {
            return 'verified' as VerificationStatus;
          }
          if (status === 'PENDING' || status === 'AUDITING' || status === 'SUBMITTED') {
            return 'pending' as VerificationStatus;
          }
          if (status === 'REJECTED' || status === 'FAILED') {
            return 'rejected' as VerificationStatus;
          }
          return 'unverified' as VerificationStatus;
        },
        get verified() {
          const status = (rawUser.certification_status || '').toUpperCase();
          return status === 'APPROVED' || status === 'PASSED' || status === 'SUCCESS';
        },
        get isVip() {
          const sub = rawUser.membership?.subscriptions?.[0];
          return sub?.plan_id === 'pro' || sub?.plan_id === 'vip' || sub?.plan_id === 'enterprise';
        },
      }
    : null;

  return {
    status: store.status,
    isLoggedIn: Boolean(store.user),
    isEnterprise: store.isEnterprise(),
    user,
    rawUser: store.user,
    error: store.error,
    login: (phone, code, accountType) => store.login({ phone, code, accountType }),
    register: (phone, code, accountType) => store.register({ phone, code, accountType }),
    loginPersonal: store.loginPersonal,
    loginEnterprise: store.loginEnterprise,
    loginWeChatPhone: store.loginWeChatPhone,
    loginWeChatSMS: store.loginWeChatSMS,
    loginAs: store.loginAs,
    updateProfile: store.updateProfile,
    logout: store.logout,
    logoutAll: store.logoutAll,
    refreshSession: store.refreshSession,
    clearError: store.clearError,
    can: store.can,
    hasRole: store.hasRole,
    hasMembership: store.hasMembership,
    hasAuthorizedContactView: store.hasAuthorizedContactView(),
    canViewAuthorizedContact: store.hasAuthorizedContactView,
  };
}

